// Axes helpers: coordinate system axes and vector config arrows

import * as THREE from 'three';
import { threeJSToPhysical, mapPhysicalToThreeWithRotation } from '@adapters/coordinates';
import { AXIS_LABEL_DEFAULTS } from '../config/defaults';
import type { ViewerContext } from '../app/context';
import type { ThemeDefaults } from '../config/defaults';

export interface AxisVectorConfig {
    direction: { x: number; y: number; z: number };
    color?: string;
    length?: number;
    showCone?: boolean;
    coneLength?: number;
    coneRadius?: number;
    radius?: number;
    label?: string;
    labelColor?: string;
    labelSize?: number;
    labelWeight?: string | number;
    labelOpacity?: number;
    labelPadding?: number;
}

export interface AxesVectorsConfig {
    vectors: AxisVectorConfig[];
    origin?: { x: number; y: number; z: number };
    showCones?: boolean;
    coneRadius?: number;
    coneLength?: number;
    label?: {
        color?: string;
        size?: number;
        opacity?: number;
        padding?: number;
        weight?: string | number;
    };
    rescaleWithData?: boolean;
    rescalePadding?: number;
}

interface SceneRegistryHandle {
    register: (entry: {
        name: string;
        type: string;
        group: string;
        threeObject: THREE.Object3D;
        position: { x: number; y: number; z: number };
    }) => void;
}

/**
 * Create the static XYZ axis lines + labels. `sceneRegistry.register` is
 * passed so coordinate-rotation changes can replay the spawn positions.
 */
export function applyCoordinateSystem(viewer: ViewerContext, sceneRegistry: SceneRegistryHandle | null): void {
    if (!viewer.three.scene) return;
    createCoordinateSystem({
        THREE,
        scene: viewer.three.scene,
        registerSceneObject: sceneRegistry ? sceneRegistry.register : undefined,
        threeJSToPhysical,
        coordLabels: viewer.objects.axes.coordLabels,
    });
}

/**
 * Replace the current axes-vectors group with one built from `cfg`. Skips when
 * cfg is missing or has no vectors. Disposes the previous group first.
 */
// Toggle visibility of the axes-vectors group, lazy-loading it from
// `viewer.sceneMeta.axesVectorsConfig` the first time it's switched on (the
// group is built on demand rather than at scene init).
export function setAxesVectorsVisible(viewer: ViewerContext, visible: boolean): void {
    const group = viewer.objects.axes.vectorsGroup;
    if (group) group.visible = visible;
    if (visible
        && viewer.sceneMeta.showCoordAxes
        && viewer.sceneMeta.axesVectorsConfig
        && (!group || !group.parent)
    ) {
        applyAxesVectors(viewer, viewer.sceneMeta.axesVectorsConfig as unknown as AxesVectorsConfig);
    }
}

export function applyAxesVectors(viewer: ViewerContext, cfg: AxesVectorsConfig | null | undefined): void {
    if (!cfg || !Array.isArray(cfg.vectors)) return;
    const scene = viewer.three.scene;
    if (!scene) return;
    const prev = viewer.objects.axes.vectorsGroup;
    if (prev && prev.parent) {
        try { scene.remove(prev); } catch { /* swallow */ }
        viewer.objects.axes.vectorsGroup = null;
    }
    const grid = viewer.grid;
    const themedCfg = applyThemeAxisColors(cfg, viewer.theme.current);
    viewer.objects.axes.vectorsGroup = loadAxesVectorsFromConfig(
        { THREE, scene, THEME: viewer.theme.current,
          GRID_X_MIN: grid.xMin, GRID_X_MAX: grid.xMax,
          GRID_Y_MIN: grid.yMin, GRID_Y_MAX: grid.yMax,
          GRID_Z_MIN: grid.zMin, GRID_Z_MAX: grid.zMax,
          mapPhysicalToThreeWithRotation,
          coordLabels: viewer.objects.axes.coordLabels },
        themedCfg
    ) || viewer.objects.axes.vectorsGroup;
}

// Overlay theme.axes.{x,y,z} hex colors onto each vector by label. scene_default's
// axesVectors entries remain the fallback when the theme lacks an axis color.
function applyThemeAxisColors(cfg: AxesVectorsConfig, theme: ThemeDefaults | null): AxesVectorsConfig {
    const axes = theme && theme.axes;
    if (!axes) return cfg;
    const byLabel: Record<string, string | null | undefined> = { X: axes.x, Y: axes.y, Z: axes.z };
    return {
        ...cfg,
        vectors: cfg.vectors.map((v) => {
            const override = v && typeof v.label === 'string' ? byLabel[v.label.toUpperCase()] : null;
            return override ? { ...v, color: override } : v;
        }),
    };
}

interface CoordinateSystemCtx {
    THREE: typeof import('three');
    scene: THREE.Scene;
    registerSceneObject?: SceneRegistryHandle['register'] | undefined;
    threeJSToPhysical: (x: number, y: number, z: number) => THREE.Vector3;
    coordLabels: THREE.Object3D[];
}

export function createCoordinateSystem(ctx: CoordinateSystemCtx): void {
    const { THREE, scene, registerSceneObject, threeJSToPhysical, coordLabels } = ctx;
    function createAxisLine(start: THREE.Vector3, end: THREE.Vector3, color: number): THREE.Line {
        const geometry = new THREE.BufferGeometry().setFromPoints([start, end]);
        const material = new THREE.LineBasicMaterial({ color: color, linewidth: 3 });
        const line = new THREE.Line(geometry, material);
        if (registerSceneObject) registerSceneObject({ name: 'AxisLine', type: 'axis', group: 'axes', threeObject: line, position: { x: 0, y: 0, z: 0 } });
        return line;
    }
    function createAxisLabel(text: string, position: THREE.Vector3, color: string): THREE.Mesh {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = 128; canvas.height = 128;
        if (context) {
            context.clearRect(0, 0, 128, 128);
            context.fillStyle = color;
            const baseWorldSize = 0.8; const worldSize = 1.0;
            const fontPx = Math.max(48, Math.round(64 * (worldSize / baseWorldSize)));
            context.font = `bold ${fontPx}px Arial`;
            context.textAlign = 'center'; context.textBaseline = 'middle';
            context.fillText(text, 64, 64);
        }
        const worldSize = 1.0;
        const texture = new THREE.CanvasTexture(canvas); texture.premultiplyAlpha = true; texture.generateMipmaps = false; texture.minFilter = THREE.LinearFilter; texture.magFilter = THREE.LinearFilter;
        const material = new THREE.MeshBasicMaterial({ map: texture, transparent: true, opacity: AXIS_LABEL_DEFAULTS.opacity, depthTest: AXIS_LABEL_DEFAULTS.depthTest, depthWrite: AXIS_LABEL_DEFAULTS.depthWrite, polygonOffset: AXIS_LABEL_DEFAULTS.polygonOffset, polygonOffsetFactor: AXIS_LABEL_DEFAULTS.polygonOffsetFactor, alphaTest: AXIS_LABEL_DEFAULTS.alphaTest });
        const geometry = new THREE.PlaneGeometry(worldSize, worldSize);
        const label = new THREE.Mesh(geometry, material);
        label.userData['physicalPosition'] = threeJSToPhysical(position.x, position.y, position.z);
        if (registerSceneObject) registerSceneObject({ name: `AxisLabel_${text}`, type: 'label', group: 'axes', threeObject: label, position: label.userData['physicalPosition'] as { x: number; y: number; z: number } });
        label.position.copy(position);
        scene.add(label);
        coordLabels.push(label);
        return label;
    }
    const xLine = createAxisLine(new THREE.Vector3(0,0,0), new THREE.Vector3(0,0,8), 0xff0000); scene.add(xLine);
    const xMarker = new THREE.Mesh(new THREE.SphereGeometry(0.1, 8, 8), new THREE.MeshBasicMaterial({ color: 0xff0000 }));
    xMarker.userData['physicalPosition'] = threeJSToPhysical(0, 0, 8); scene.add(xMarker); createAxisLabel('X', new THREE.Vector3(0, 0, 8.5), '#ff0000');
    const yLine = createAxisLine(new THREE.Vector3(0,0,0), new THREE.Vector3(8,0,0), 0x00ff00); scene.add(yLine);
    const yMarker = new THREE.Mesh(new THREE.SphereGeometry(0.1, 8, 8), new THREE.MeshBasicMaterial({ color: 0x00ff00 }));
    yMarker.userData['physicalPosition'] = threeJSToPhysical(8, 0, 0); scene.add(yMarker); createAxisLabel('Y', new THREE.Vector3(8.5, 0, 0), '#00ff00');
    const zLine = createAxisLine(new THREE.Vector3(0,0,0), new THREE.Vector3(0,8,0), 0x0000ff); scene.add(zLine);
    const zMarker = new THREE.Mesh(new THREE.SphereGeometry(0.1, 8, 8), new THREE.MeshBasicMaterial({ color: 0x0000ff }));
    zMarker.userData['physicalPosition'] = threeJSToPhysical(0, 8, 0); scene.add(zMarker); createAxisLabel('Z', new THREE.Vector3(0, 8.5, 0), '#0000ff');
}

interface AxesVectorsCtx {
    THREE: typeof import('three');
    scene: THREE.Scene;
    THEME: ThemeDefaults | null;
    GRID_X_MIN: number; GRID_X_MAX: number;
    GRID_Y_MIN: number; GRID_Y_MAX: number;
    GRID_Z_MIN: number; GRID_Z_MAX: number;
    mapPhysicalToThreeWithRotation: (p: THREE.Vector3) => THREE.Vector3;
    coordLabels: THREE.Object3D[];
}

export function loadAxesVectorsFromConfig(ctx: AxesVectorsCtx, cfg: AxesVectorsConfig): THREE.Group | null {
    const { THREE, scene, THEME, GRID_X_MIN, GRID_X_MAX, GRID_Y_MIN, GRID_Y_MAX, GRID_Z_MIN, GRID_Z_MAX,
            mapPhysicalToThreeWithRotation, coordLabels } = ctx;
    if (!cfg || !Array.isArray(cfg.vectors)) return null;
    const showConesGlobal = cfg.showCones !== false;
    const defaultConeRadius = (typeof cfg.coneRadius === 'number') ? cfg.coneRadius : null;
    const defaultConeLength = (typeof cfg.coneLength === 'number') ? cfg.coneLength : null;
    const labelDefaults = cfg.label || {};
    const defaultLabelWeight = typeof labelDefaults.weight === 'string' ? labelDefaults.weight : 'bold';
    const defaultLabelOpacity = typeof labelDefaults.opacity === 'number' ? labelDefaults.opacity : 0.9;
    const defaultLabelPadding = typeof labelDefaults.padding === 'number' ? labelDefaults.padding : 0.3;
    const rescale = cfg.rescaleWithData === true;
    const pad = (typeof cfg.rescalePadding === 'number') ? cfg.rescalePadding : 1.0;
    const group = new THREE.Group();
    const origin = cfg.origin ? new THREE.Vector3(cfg.origin.x, cfg.origin.y, cfg.origin.z) : new THREE.Vector3(0, 0, 0);
    const distPosX = Math.max(0, GRID_X_MAX - origin.x), distNegX = Math.max(0, origin.x - GRID_X_MIN);
    const distPosY = Math.max(0, GRID_Y_MAX - origin.y), distNegY = Math.max(0, origin.y - GRID_Y_MIN);
    const distPosZ = Math.max(0, GRID_Z_MAX - origin.z), distNegZ = Math.max(0, origin.z - GRID_Z_MIN);
    cfg.vectors.forEach((v) => {
        const color = new THREE.Color(v.color || '#ffffff');
        const dirPhys = new THREE.Vector3(v.direction.x, v.direction.y, v.direction.z).normalize();
        const baseLen = typeof v.length === 'number' ? v.length : 8;
        let length = baseLen;
        if (rescale) {
            const ax = Math.abs(dirPhys.x), ay = Math.abs(dirPhys.y), az = Math.abs(dirPhys.z);
            if (ax >= ay && ax >= az) { length = (dirPhys.x >= 0 ? distPosX : distNegX) + pad; }
            else if (ay >= ax && ay >= az) { length = (dirPhys.y >= 0 ? distPosY : distNegY) + pad; }
            else { length = (dirPhys.z >= 0 ? distPosZ : distNegZ) + pad; }
        }
        const tipPhys = origin.clone().addScaledVector(dirPhys, length);
        const originThree = mapPhysicalToThreeWithRotation(origin);
        const tipThree = mapPhysicalToThreeWithRotation(tipPhys);
        const shaftGeom = new THREE.BufferGeometry().setFromPoints([originThree, tipThree]);
        const shaftMat = new THREE.LineBasicMaterial({ color: color.getHex(), linewidth: 3 });
        const shaft = new THREE.Line(shaftGeom, shaftMat); group.add(shaft);
        const showCone = typeof v.showCone === 'boolean' ? v.showCone : showConesGlobal;
        if (showCone) {
            const headLen = (typeof v.coneLength === 'number') ? v.coneLength : (defaultConeLength !== null ? defaultConeLength : Math.min(0.25 * length, 0.6));
            const headRadius = (typeof v.coneRadius === 'number') ? v.coneRadius : (defaultConeRadius !== null ? defaultConeRadius : (v.radius ? v.radius : Math.min(0.06 * length, 0.12)));
            const coneGeom = new THREE.ConeGeometry(headRadius, headLen, 16);
            const coneMat = new THREE.MeshBasicMaterial({ color: color.getHex() });
            const cone = new THREE.Mesh(coneGeom, coneMat);
            cone.position.copy(tipThree);
            const axis = new THREE.Vector3(0, 1, 0);
            const quat = new THREE.Quaternion().setFromUnitVectors(axis, tipThree.clone().sub(originThree).normalize());
            cone.quaternion.copy(quat);
            group.add(cone);
        }

        // Label near the tip
        const labelText = typeof v.label === 'string' ? v.label : '';
        if (labelText) {
            const themeVectorColor = (THEME && THEME.scene && THEME.scene.vectorLabelColor) ? THEME.scene.vectorLabelColor : null;
            const labelColorCss = v.labelColor || themeVectorColor || color.getStyle();
            const size = (typeof v.labelSize === 'number') ? v.labelSize : (typeof labelDefaults.size === 'number' ? labelDefaults.size : 1.0);
            const widthWorld = size;
            const heightWorld = Math.max(size * 0.35, 0.3);
            const fontPx = Math.max(24, Math.round(48 * (widthWorld / 0.8)));
            const weightRaw = (typeof v.labelWeight !== 'undefined') ? v.labelWeight : defaultLabelWeight;
            const weight = (typeof weightRaw === 'number') ? String(weightRaw) : (typeof weightRaw === 'string' ? weightRaw : 'bold');
            const opacity = (typeof v.labelOpacity === 'number') ? v.labelOpacity : defaultLabelOpacity;
            const padding = (typeof v.labelPadding === 'number') ? v.labelPadding : defaultLabelPadding;
            const canvas = document.createElement('canvas');
            const cctx = canvas.getContext('2d');
            canvas.width = 128; canvas.height = 64;
            if (cctx) {
                cctx.clearRect(0, 0, 128, 64);
                cctx.fillStyle = labelColorCss;
                cctx.globalAlpha = opacity;
                cctx.font = `${weight} ${fontPx}px "Helvetica Neue", Arial, sans-serif`;
                cctx.textAlign = 'center'; cctx.textBaseline = 'middle';
                cctx.fillText(labelText, 64, 32);
            }
            const tex = new THREE.CanvasTexture(canvas);
            tex.premultiplyAlpha = true; tex.generateMipmaps = false; tex.minFilter = THREE.LinearFilter; tex.magFilter = THREE.LinearFilter;
            const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, opacity: AXIS_LABEL_DEFAULTS.opacity, depthTest: AXIS_LABEL_DEFAULTS.depthTest, depthWrite: AXIS_LABEL_DEFAULTS.depthWrite, polygonOffset: AXIS_LABEL_DEFAULTS.polygonOffset, polygonOffsetFactor: AXIS_LABEL_DEFAULTS.polygonOffsetFactor, alphaTest: AXIS_LABEL_DEFAULTS.alphaTest });
            const geo = new THREE.PlaneGeometry(widthWorld, heightWorld);
            const mesh = new THREE.Mesh(geo, mat);
            const tipDir = tipThree.clone().sub(originThree).normalize();
            const offset = tipDir.multiplyScalar(padding);
            mesh.position.copy(tipThree.clone().add(offset));
            group.add(mesh);
            coordLabels.push(mesh);
        }
    });
    scene.add(group);
    return group;
}
